import { useState, useRef } from 'react'
import { apiUrl, authFetch } from '../../lib/api'
import { useAppStore } from '../../store/appStore'

export function ChatImageButton() {
  const [image, setImage] = useState<string | null>(null)
  const [prompt, setPrompt] = useState('')
  const [loading, setLoading] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const promptRef = useRef<HTMLInputElement>(null)
  const { addMessage } = useAppStore()

  const handleFile = (file: File) => {
    if (!file.type.startsWith('image/')) return
    if (file.size > 4 * 1024 * 1024) { alert('Image too large (max 4MB)'); return }
    const reader = new FileReader()
    reader.onload = () => {
      setImage(reader.result as string)
      setTimeout(() => promptRef.current?.focus(), 100)
    }
    reader.readAsDataURL(file)
  }

  const handleSend = async () => {
    if (!image || loading) return
    const question = prompt.trim() || 'Analyze this image and generate the matching code'
    setLoading(true)

    addMessage({ role: 'user', content: `[Image] ${question}` })

    try {
      const res = await authFetch(apiUrl('/api/ai-tools/analyze-image'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image, prompt: question }),
      })
      const data = await res.json()
      addMessage({ role: 'assistant', content: data.result ?? data.error ?? 'Failed to analyze' })
    } catch {
      addMessage({ role: 'assistant', content: 'Failed to analyze image' })
    } finally {
      setLoading(false)
      setImage(null)
      setPrompt('')
    }
  }

  if (image) {
    return (
      <div style={{
        position: 'absolute', bottom: '100%', left: 0, right: 0, marginBottom: 4,
        background: 'var(--bg-1)', border: '1px solid var(--accent)', borderRadius: 10,
        boxShadow: '0 -4px 16px rgba(0,0,0,0.3)', padding: 10, zIndex: 100,
      }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <img src={image} alt="Upload" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border)' }} />
          <div style={{ flex: 1 }}>
            <input
              ref={promptRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); if (e.key === 'Escape') { setImage(null); setPrompt('') } }}
              placeholder="What should AI do with this image?"
              style={{
                width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-2)',
                background: 'var(--bg-2)', color: 'var(--text-0)', fontFamily: 'var(--font-mono)',
                fontSize: 11, outline: 'none',
              }}
            />
            <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 4 }}>
              Enter to send, Escape to cancel
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => { setImage(null); setPrompt('') }} style={cancelBtn}>Cancel</button>
          <button onClick={handleSend} disabled={loading} style={sendBtn}>
            {loading ? 'Analyzing...' : 'Send with Image'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <button
        onClick={() => fileRef.current?.click()}
        disabled={loading}
        title="Upload image / screenshot"
        style={{
          width: 26, height: 26, borderRadius: 7, border: 'none',
          background: 'none', cursor: 'pointer', color: 'var(--text-2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          transition: 'color 0.12s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--accent)' }}
        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-2)' }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
        </svg>
      </button>
      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); e.target.value = '' }}
      />
    </>
  )
}

const cancelBtn: React.CSSProperties = {
  padding: '5px 12px', borderRadius: 6, border: '1px solid var(--border-2)',
  background: 'none', color: 'var(--text-2)', cursor: 'pointer',
  fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
}
const sendBtn: React.CSSProperties = {
  flex: 1, padding: '5px 12px', borderRadius: 6, border: '1px solid var(--accent)',
  background: 'rgba(124,110,245,0.1)', color: 'var(--accent)', cursor: 'pointer',
  fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
}
